import { Bar, FrontDesk, Housekeeping, Kiosk, Location, Room, Staff } from '@era/locations-sdk';
import { Action } from '@ngrx/store';

/**
 * Shared types used across multiple store modules
 */

/**
 * Represents the different category types in the system.
 */
export type CategoryType =
  | 'Brand'
  | 'Location'
  | 'Room'
  | 'Bar'
  | 'FrontDesk'
  | 'Housekeeping'
  | 'Kiosk'
  | 'Staff'
  | 'None';

export interface Brand {
  id: string;
  displayName: string;
  uniqueId: string;
  humanId: string;
}

/**
 * Type mapping from CategoryType to its corresponding Entity type
 * Note: Using type instead of interface is required for mapped types
 */
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type CategoryTypeToEntity = {
  Brand: Brand;
  Location: Location;
  Room: Room;
  Bar: Bar;
  FrontDesk: FrontDesk;
  Housekeeping: Housekeeping;
  Kiosk: Kiosk;
  Staff: Staff;
  None: never;
};

/**
 * Type guard to check if a string is a valid CategoryType
 */
export function isCategoryType(value: string): value is CategoryType {
  return ['Brand', 'Location', 'Room', 'Bar', 'FrontDesk', 'Housekeeping', 'Kiosk', 'Staff', 'None'].includes(value);
}

/**
 * Array of all valid category types for iteration
 */
export const ALL_CATEGORY_TYPES: CategoryType[] = [
  'Brand',
  'Location',
  'Room',
  'Bar',
  'FrontDesk',
  'Housekeeping',
  'Kiosk',
  'Staff',
  'None',
] as const;

// ============================================================================
// TOAST TYPES
// ============================================================================

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface IToastie {
  message: string;
  toastType: ToastType;
  showNotification: boolean;
}

/**
 * Type guard to check if an action should show a toast notification
 * Checks that the action has toast properties AND showNotification is true
 */
export function isToastieAction(action: Action): action is Action & { toast: IToastie } {
  if (!action || typeof action !== 'object' || !('toast' in action)) {
    return false;
  }

  const toast = (action as Record<string, unknown>).toast;
  return (
    toast !== null &&
    typeof toast === 'object' &&
    'message' in toast &&
    'toastType' in toast &&
    'showNotification' in toast &&
    typeof toast.message === 'string' &&
    ['success', 'error', 'info', 'warning'].includes(toast.toastType as ToastType) &&
    typeof toast.showNotification === 'boolean' &&
    toast.showNotification === true
  );
}
